import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  GraduationCap,
  Users,
  Award,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Search,
  Download,
  BarChart3,
  Target,
  BookOpen,
  ChevronRight,
  UserCheck,
  UserX,
  Mail,
  RefreshCw,
  Shield,
  Calendar,
  Flame,
  Eye
} from "lucide-react";

export default function CEUDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [staffProgress, setStaffProgress] = useState([]);
  const [needsAttention, setNeedsAttention] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffDetail, setStaffDetail] = useState(null);
  const [showStaffDetail, setShowStaffDetail] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("completion_rate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("all");
  
  const allowedRoles = ["admin", "administrator", "staff"];

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    
    loadDashboardData();
  }, [user, navigate, authLoading]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, staffRes, attentionRes] = await Promise.all([
        api.get("/academy/dashboard/overview"),
        api.get(`/academy/dashboard/staff-progress?sort_by=${sortBy}&sort_order=${sortOrder}&filter_status=${filterStatus}`),
        api.get("/academy/dashboard/needs-attention")
      ]);
      
      setOverview(overviewRes.data);
      setStaffProgress(staffRes.data.staff || []);
      setNeedsAttention(attentionRes.data);
      
      // Load compliance report for admins
      if (user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrator") {
        const reportRes = await api.get("/academy/dashboard/compliance-report");
        setComplianceReport(reportRes.data);
      }
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const loadStaffDetail = async (userId) => {
    try {
      const response = await api.get(`/academy/dashboard/staff/${userId}/details`);
      setStaffDetail(response.data);
      setShowStaffDetail(true);
    } catch (error) {
      console.error("Failed to load staff detail:", error);
      toast.error("Failed to load staff details");
    }
  };

  const filteredStaff = staffProgress.filter(staff => 
    staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      compliant: { bg: "bg-green-100", text: "text-green-700", label: "Compliant" },
      in_progress: { bg: "bg-blue-100", text: "text-blue-700", label: "In Progress" },
      needs_attention: { bg: "bg-amber-100", text: "text-amber-700", label: "Needs Attention" },
      not_started: { bg: "bg-gray-100", text: "text-gray-700", label: "Not Started" }
    };
    const style = styles[status] || styles.not_started;
    return (
      <Badge className={`${style.bg} ${style.text} border-0`}>
        {style.label}
      </Badge>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-muted-foreground">Loading CEU Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold">CEU Progress Dashboard</h1>
              </div>
              <p className="text-teal-100">Track staff training compliance and CEU credits across the organization</p>
            </div>
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              onClick={loadDashboardData}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 mx-auto text-teal-600 mb-2" />
                <p className="text-2xl font-bold text-teal-700">{overview.organization_stats.total_staff}</p>
                <p className="text-xs text-muted-foreground">Total Staff</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <Shield className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <p className="text-2xl font-bold text-green-700">{overview.organization_stats.compliance_rate}%</p>
                <p className="text-xs text-muted-foreground">Compliance Rate</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-blue-700">{overview.organization_stats.compliant_staff}</p>
                <p className="text-xs text-muted-foreground">Fully Compliant</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <BookOpen className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-purple-700">{overview.organization_stats.total_courses_completed}</p>
                <p className="text-xs text-muted-foreground">Courses Completed</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <Award className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                <p className="text-2xl font-bold text-amber-700">{overview.organization_stats.total_ceu_earned}</p>
                <p className="text-xs text-muted-foreground">Total CEUs Earned</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                <p className="text-2xl font-bold text-red-700">{overview.organization_stats.staff_not_started}</p>
                <p className="text-xs text-muted-foreground">Not Started</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Needs Attention Alert */}
        {needsAttention && needsAttention.total_needing_attention > 0 && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{needsAttention.total_needing_attention} staff members</strong> need attention: 
              {needsAttention.not_started.count > 0 && ` ${needsAttention.not_started.count} haven't started,`}
              {needsAttention.low_progress.count > 0 && ` ${needsAttention.low_progress.count} have low progress,`}
              {needsAttention.inactive.count > 0 && ` ${needsAttention.inactive.count} are inactive.`}
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="staff" className="space-y-6">
          <TabsList className="bg-white/80 p-1">
            <TabsTrigger value="staff" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Staff Progress
            </TabsTrigger>
            <TabsTrigger value="attention" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Needs Attention
            </TabsTrigger>
            <TabsTrigger value="tracks" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <Target className="h-4 w-4 mr-2" />
              Track Analysis
            </TabsTrigger>
            {(user?.role === "admin" || user?.role === "administrator") && (
              <TabsTrigger value="compliance" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <Shield className="h-4 w-4 mr-2" />
                Compliance Report
              </TabsTrigger>
            )}
          </TabsList>

          {/* Staff Progress Tab */}
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Staff Training Progress</CardTitle>
                    <CardDescription>View and manage individual staff training status</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search staff..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-[200px]"
                      />
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="compliant">Compliant</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="needs_attention">Needs Attention</SelectItem>
                        <SelectItem value="not_started">Not Started</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredStaff.map((staff) => (
                    <div 
                      key={staff.user_id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-teal-700 font-semibold">
                            {staff.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{staff.name}</p>
                          <p className="text-sm text-muted-foreground">{staff.role} • {staff.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-teal-700">{staff.completion_rate}%</p>
                          <p className="text-xs text-muted-foreground">Completion</p>
                        </div>
                        
                        <div className="text-center">
                          <p className="text-lg font-semibold text-purple-700">{staff.ceu_earned}</p>
                          <p className="text-xs text-muted-foreground">CEUs</p>
                        </div>
                        
                        <div className="w-32">
                          <Progress value={staff.completion_rate} className="h-2" />
                          <p className="text-xs text-muted-foreground mt-1">
                            {staff.completed_required}/{staff.total_required} required
                          </p>
                        </div>
                        
                        {getStatusBadge(staff.status)}
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => loadStaffDetail(staff.user_id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {filteredStaff.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No staff members match your search criteria
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Needs Attention Tab */}
          <TabsContent value="attention">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Not Started */}
              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <UserX className="h-5 w-5" />
                    Not Started ({needsAttention?.not_started.count || 0})
                  </CardTitle>
                  <CardDescription>Staff who haven't begun training</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {needsAttention?.not_started.staff.map((staff) => (
                      <div key={staff.user_id} className="p-3 bg-red-50 rounded-lg">
                        <p className="font-medium">{staff.name}</p>
                        <p className="text-sm text-muted-foreground">{staff.role}</p>
                        <p className="text-xs text-red-600 mt-1">{staff.reason}</p>
                      </div>
                    ))}
                    {(!needsAttention?.not_started.staff || needsAttention.not_started.staff.length === 0) && (
                      <p className="text-center text-muted-foreground py-4">No staff in this category</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Low Progress */}
              <Card className="border-amber-200">
                <CardHeader className="bg-amber-50">
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <TrendingUp className="h-5 w-5" />
                    Low Progress ({needsAttention?.low_progress.count || 0})
                  </CardTitle>
                  <CardDescription>Staff with less than 25% completion</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {needsAttention?.low_progress.staff.map((staff) => (
                      <div key={staff.user_id} className="p-3 bg-amber-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{staff.name}</p>
                            <p className="text-sm text-muted-foreground">{staff.role}</p>
                          </div>
                          <Badge variant="outline" className="text-amber-700">
                            {staff.completion_rate}%
                          </Badge>
                        </div>
                        <p className="text-xs text-amber-600 mt-1">{staff.reason}</p>
                      </div>
                    ))}
                    {(!needsAttention?.low_progress.staff || needsAttention.low_progress.staff.length === 0) && (
                      <p className="text-center text-muted-foreground py-4">No staff in this category</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Inactive */}
              <Card className="border-gray-200">
                <CardHeader className="bg-gray-50">
                  <CardTitle className="flex items-center gap-2 text-gray-700">
                    <Clock className="h-5 w-5" />
                    Inactive ({needsAttention?.inactive.count || 0})
                  </CardTitle>
                  <CardDescription>No activity in {needsAttention?.inactive.threshold_days || 14} days</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {needsAttention?.inactive.staff.map((staff) => (
                      <div key={staff.user_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{staff.name}</p>
                            <p className="text-sm text-muted-foreground">{staff.role}</p>
                          </div>
                          <Badge variant="outline">
                            {staff.completion_rate}%
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">Last active: {staff.last_activity}</p>
                      </div>
                    ))}
                    {(!needsAttention?.inactive.staff || needsAttention.inactive.staff.length === 0) && (
                      <p className="text-center text-muted-foreground py-4">No staff in this category</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Track Analysis Tab */}
          <TabsContent value="tracks">
            <Card>
              <CardHeader>
                <CardTitle>Training Track Completion Rates</CardTitle>
                <CardDescription>Overview of completion rates by training track</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {overview?.track_completion && Object.entries(overview.track_completion).map(([track, data]) => (
                    <div key={track} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h3 className="font-semibold">{track}</h3>
                          <p className="text-sm text-muted-foreground">{data.total_courses} courses</p>
                        </div>
                        <span className="text-2xl font-bold text-teal-700">{data.completion_rate}%</span>
                      </div>
                      <Progress value={data.completion_rate} className="h-3" />
                      <p className="text-xs text-muted-foreground mt-2">
                        {data.completions} total completions across all staff
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Report Tab */}
          {(user?.role === "admin" || user?.role === "administrator") && (
            <TabsContent value="compliance">
              {complianceReport && (
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Compliance Report</CardTitle>
                          <CardDescription>
                            Generated: {new Date(complianceReport.report_date).toLocaleString()}
                          </CardDescription>
                        </div>
                        <Button variant="outline">
                          <Download className="h-4 w-4 mr-2" />
                          Export Report
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-6 mb-6">
                        <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                          <p className="text-4xl font-bold text-green-700">
                            {complianceReport.organization_compliance.compliance_rate}%
                          </p>
                          <p className="text-green-600 font-medium">Organization Compliance</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {complianceReport.organization_compliance.fully_compliant} of {complianceReport.organization_compliance.total_staff} staff
                          </p>
                        </div>
                        
                        <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                          <p className="text-4xl font-bold text-purple-700">
                            {complianceReport.required_courses_count}
                          </p>
                          <p className="text-purple-600 font-medium">Required Courses</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {complianceReport.total_ceu_required} CEUs required
                          </p>
                        </div>
                        
                        <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                          <p className="text-4xl font-bold text-blue-700">
                            {Object.keys(complianceReport.compliance_by_role).length}
                          </p>
                          <p className="text-blue-600 font-medium">Staff Roles</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Tracked in system
                          </p>
                        </div>
                      </div>

                      {/* Compliance by Role */}
                      <h3 className="font-semibold mb-4">Compliance by Role</h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {Object.entries(complianceReport.compliance_by_role).map(([role, data]) => (
                          <div key={role} className="p-4 border rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium capitalize">{role}</span>
                              <span className="text-lg font-bold text-teal-700">
                                {data.total > 0 ? Math.round(data.compliant / data.total * 100) : 0}%
                              </span>
                            </div>
                            <Progress 
                              value={data.total > 0 ? (data.compliant / data.total * 100) : 0} 
                              className="h-2 mb-2" 
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{data.compliant} compliant</span>
                              <span>{data.in_progress} in progress</span>
                              <span>{data.not_started} not started</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Course Completion Rates */}
                      <h3 className="font-semibold mb-4">Course Completion Rates (Lowest First)</h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {complianceReport.course_completion.slice(0, 15).map((course) => (
                          <div key={course.course_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{course.title}</p>
                                {course.required && (
                                  <Badge variant="outline" className="text-xs">Required</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{course.track} • {course.ceu} CEU</p>
                            </div>
                            <div className="w-32">
                              <Progress value={course.completion_rate} className="h-2" />
                            </div>
                            <span className={`text-sm font-medium w-16 text-right ${
                              course.completion_rate < 50 ? 'text-red-600' : 
                              course.completion_rate < 75 ? 'text-amber-600' : 'text-green-600'
                            }`}>
                              {course.completion_rate}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Staff Detail Dialog */}
      <Dialog open={showStaffDetail} onOpenChange={setShowStaffDetail}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {staffDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <span className="text-teal-700 font-bold text-lg">
                      {staffDetail.user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p>{staffDetail.user.name}</p>
                    <p className="text-sm font-normal text-muted-foreground">
                      {staffDetail.user.role} • {staffDetail.user.email}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4 my-4">
                <div className="text-center p-3 bg-teal-50 rounded-lg">
                  <p className="text-2xl font-bold text-teal-700">{staffDetail.summary.completion_rate}%</p>
                  <p className="text-xs text-muted-foreground">Overall</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{staffDetail.summary.ceu_earned}</p>
                  <p className="text-xs text-muted-foreground">CEUs Earned</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">
                    {staffDetail.summary.required_completed}/{staffDetail.summary.required_total}
                  </p>
                  <p className="text-xs text-muted-foreground">Required</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1">
                    <Flame className="h-5 w-5 text-amber-600" />
                    <p className="text-2xl font-bold text-amber-700">{staffDetail.summary.streak}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Day Streak</p>
                </div>
              </div>

              {/* Compliance Status */}
              <div className={`p-3 rounded-lg mb-4 ${
                staffDetail.summary.is_compliant 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center gap-2">
                  {staffDetail.summary.is_compliant ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-700">Fully Compliant</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-700">
                        {staffDetail.summary.required_total - staffDetail.summary.required_completed} required courses remaining
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Track Progress */}
              <h3 className="font-semibold mb-3">Progress by Track</h3>
              <div className="space-y-3">
                {Object.entries(staffDetail.tracks).map(([trackName, track]) => (
                  <div key={trackName} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{trackName}</span>
                      <span className="text-sm">
                        {track.completed}/{track.total} courses • {track.ceu_earned} CEU
                      </span>
                    </div>
                    <Progress value={track.completion_rate} className="h-2" />
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
